import { createFileRoute, Link } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../lib/auth/auth-context';
import { supabase as defaultClient } from '../lib/supabase';
import { captureSupabaseError } from '../lib/sentry-supabase';
import type { LynrxSupabaseClient } from '@lynrx/shared';
import { Alert } from '../components/ui/Alert';
import { DataTable, type Column } from '../components/ui/DataTable';
import { useSetPageHeader } from '../components/layout/PageHeaderContext';

// Pharmacies list — admin pharmacy profile Slice 1, /admin/pharmacy.
// Same RLS-scoped list pattern as Patients and Audit: the browser
// supabase client filters by the caller's tenant via the
// `pharmacies_select_own_tenant` policy; application code never names
// tenant_id. A list of one is just a list of one — no auto-resolution
// or conditional picker.
//
// Editable surface lives on the detail page
// (/admin/pharmacy/$pharmacyId); the list is purely navigational.

const PHARMACY_LIST_COLUMNS =
  'id, name, address, city, state, zip, timezone';

export type PharmacyListRow = {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  timezone: string;
};

export const Route = createFileRoute('/_authenticated/admin/pharmacy/')({
  component: PharmaciesListPage,
});

function PharmaciesListPage(): JSX.Element {
  return <PharmaciesListView />;
}

type ViewProps = {
  // Test seam — defaults to the singleton browser supabase client.
  client?: LynrxSupabaseClient;
};

function addressDisplay(row: PharmacyListRow): string {
  const cityState = [row.city, row.state].filter(Boolean).join(', ');
  const parts = [row.address ?? '', cityState, row.zip ?? ''].filter(
    (p) => p && p.trim().length > 0,
  );
  return parts.length > 0 ? parts.join(' · ') : '—';
}

export function PharmaciesListView({
  client = defaultClient,
}: ViewProps): JSX.Element {
  const { session, isReady } = useAuth();
  useSetPageHeader({
    title: 'Pharmacies',
    subtitle:
      'Pharmacy profile and settings. Click a pharmacy to view or edit.',
  });

  const [rows, setRows] = useState<PharmacyListRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchRows = useCallback(async () => {
    if (!session) return;
    setError(null);
    const { data, error: err } = await client
      .from('pharmacies')
      .select(PHARMACY_LIST_COLUMNS)
      .is('deleted_at', null)
      .order('name', { ascending: true });
    if (err) {
      captureSupabaseError(err, 'admin.pharmacy.list.fetch');
      setError('Could not load pharmacies.');
      return;
    }
    setRows((data ?? []) as unknown as PharmacyListRow[]);
  }, [client, session]);

  useEffect(() => {
    if (!isReady || !session) return;
    void fetchRows();
  }, [isReady, session, fetchRows]);

  const columns: Column<PharmacyListRow>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Name',
        render: (r) => (
          <Link
            to="/admin/pharmacy/$pharmacyId"
            params={{ pharmacyId: r.id }}
            className="font-medium text-ink underline-offset-4 hover:underline"
          >
            {r.name}
          </Link>
        ),
      },
      { key: 'address', header: 'Address', render: addressDisplay },
      { key: 'timezone', header: 'Timezone', render: (r) => r.timezone },
    ],
    [],
  );

  return (
    <>
      {error ? (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={rows ?? []}
        rowKey={(r) => r.id}
        caption="Pharmacies"
        emptyMessage={
          rows === null
            ? 'Loading pharmacies…'
            : 'No pharmacies accessible to this account.'
        }
      />
    </>
  );
}
